/**
 * Copyright 2022 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import type { Locale } from '@/types/i18n';
import type { useMapFacet } from '@/hooks/useMapFacet';
import { getSortValue } from './getSortValue';
import { type ParsedUrlQuery } from 'querystring';

type SearchQuery = {
  searchText: string | string[] | undefined;
  sort: string | string[] | undefined;
  facet: string | undefined;
  minPrice: string | undefined;
  maxPrice: string | undefined;
};

export const getApiOptions = ({
  query,
  locale,
  vehicleId,
  storeNumber,
  mapFacet,
  preview,
  viewingPartTypes,
  vehicleIdChanged,
  userProfileId,
  acesEndpointEnabled,
  isBrazil,
}: {
  query: ParsedUrlQuery;
  locale: Locale;
  vehicleId: string | undefined;
  storeNumber: string | undefined;
  mapFacet: ReturnType<typeof useMapFacet>;
  preview: boolean;
  isCurrentPage?: boolean;
  viewingPartTypes?: boolean;
  vehicleIdChanged?: boolean;
  userProfileId?: string;
  acesEndpointEnabled?: boolean;
  isBrazil?: boolean;
}) => {
  const { searchText, sort: sortParam = [], facet, minPrice, maxPrice } = query as SearchQuery;
  // For BR, pass sort as string; for others, normalize to object form via getSortValue
  const sort =
    typeof sortParam === 'string' ? (isBrazil ? sortParam : getSortValue(sortParam)) : undefined;

  // For ACES: pass minPrice/maxPrice separately, don't include in facet
  // For non-ACES: construct price facet (existing behavior)
  const newFacet =
    !acesEndpointEnabled && (minPrice || maxPrice)
      ? `${facet ? `${facet}++` : ''}price:${minPrice ?? '*'} to ${maxPrice ?? '*'}`
      : facet;

  // For ACES: add minPrice/maxPrice as separate fields (parsed as numbers) only if defined
  const parsedMinPrice =
    acesEndpointEnabled && typeof minPrice === 'string' ? parseInt(minPrice) : undefined;
  const parsedMaxPrice =
    acesEndpointEnabled && typeof maxPrice === 'string' ? parseInt(maxPrice) : undefined;

  return {
    mapFacet,
    locale,
    searchText: typeof searchText === 'string' ? searchText : undefined,
    vehicleId,
    userProfileId: userProfileId,
    skipPdpRedirect: vehicleIdChanged,
    facet: typeof newFacet === 'string' ? newFacet : undefined,
    // Only include minPrice/maxPrice if they have values
    ...(parsedMinPrice !== undefined && { minPrice: parsedMinPrice }),
    ...(parsedMaxPrice !== undefined && { maxPrice: parsedMaxPrice }),
    sort,
    storeId: storeNumber,
    userSegment: undefined,
    keywordSearchVisual: viewingPartTypes || undefined,
    preview,
  };
};
